/**
 * Beat interpreter — walks scenes[*].beats and emits events.
 *
 * 21 beat ops: narrate, say, set, if, choice, label, jump, end, menu,
 * bg, sprite, hide, play, stop, wait, call, scene, video, effect, animation, popup.
 *
 * Pauses on: narrate, say, choice, wait (timed), video, scene (async load).
 * Everything else executes inline.
 */

import { Blackboard, tagValue, untagValue, type Listener, type TaggedValue } from "./Blackboard";
import { callFunction, type FunctionDefinition } from "./callFunction";
import { EventBus, type ChoiceOption, type MinigameDelta } from "./EventBus";
import { evaluate, resolveTemplate, setVisitProvider, setFunctionProvider, setGameClock, type AstNode } from "./ExprEval";
import { GameClock, buildCalendarConfig, type CalendarConfig } from "./GameClock";
import { resolveBackground } from "./transitionRoom";
import { Ternary } from "./Ternary";
import { RuleEngine, type RuleDefinition } from "./RuleEngine";
import { AchievementEngine, type AchievementDef } from "./AchievementEngine";
import { ObjectSpriteEngine } from "./ObjectSpriteEngine";
import { resolveObjectVariant, resolveObjectAsset } from "./objectSprites";
import { AchievementStore, type AchievementSink } from "./AchievementStore";
import { GrantLoader, type GrantDef } from "./GrantLoader";

const DURATION_NAMES: Record<string, number> = { slow: 1.0, normal: 0.5, fast: 0.25 };

export interface CharacterEntity {
  name?: string;
  display_name?: string;
  default_sprite?: string;
  color?: string;
  voice?: string;
  desc?: string[];
  verbs?: WireVerbEntry[];
  [key: string]: unknown;
}

export interface PlaceEntity {
  name?: string;
  image?: string;
  /** A single image, or a phase-keyed map for time-of-day backgrounds (v5). */
  background?: string | Record<string, string>;
  music?: string;
  desc?: string[];
  characters?: string[];
  objects?: string[];
  exits?: WireExitEntry[];
  verbs?: WireVerbEntry[];
  /** Default render scale for this place's shelved (ambient) object sprites; per-object `shelf_scale` overrides it. */
  shelf_scale?: number;
  [key: string]: unknown;
}

export type WireVerbEntry = string | { name: string; cond?: string; cond_ast?: AstNode; hidden_when_false?: boolean; time_cost?: number };

export type WireExitEntry = string | { target: string; dir?: string; verbs?: string[]; cond_ast?: AstNode; time_cost?: number };

export interface ObjectSpriteRule {
  cond_ast: AstNode;
  variant: string;
}

export interface ObjectEntity {
  name?: string;
  desc?: string[];
  verbs?: WireVerbEntry[];
  inventory_possible?: boolean;
  weight?: number;
  icon?: string;
  /** variant name → sprite asset path (e.g. {default: "...", glowing: "..."}) */
  sprites?: Record<string, string>;
  /** priority list; first rule whose cond is T selects its variant, else "default" */
  sprite_rules?: ObjectSpriteRule[];
  /** when T, the object's in-scene sprite is suppressed */
  hidden_when_ast?: AstNode;
  /** Render scale for this object when shelved (ambient); overrides the place's `shelf_scale`. */
  shelf_scale?: number;
  [key: string]: unknown;
}

export interface Entities {
  characters: Record<string, CharacterEntity>;
  places: Record<string, PlaceEntity>;
  objects: Record<string, ObjectEntity>;
}

export interface TriggerDefinition {
  entity: string;
  verb: string;
  room: string;
  scene: string;
  cond_ast?: AstNode;
}

export interface RecipeDefinition {
  a: string;
  b: string;
  result: string;
}

export interface Globals {
  functions?: FunctionDefinition[];
  rules?: RuleDefinition[];
  triggers?: TriggerDefinition[];
  recipes?: RecipeDefinition[];
  achievements?: Record<string, AchievementDef>;
  grants?: GrantDef[];
}

/** The VN Program envelope as loaded from .vnp.json. */
export interface VnProgram {
  format: string;
  version: string;
  entry: string;
  scenes: Record<string, Scene>;
  labels: Record<string, LabelRef>;
  entities?: Entities;
  globals?: Globals;
  [key: string]: unknown;
}

export interface Scene {
  beats: Beat[];
  location?: string;
  azimuth?: number;
  x?: number;
  y?: number;
}

export interface LabelRef {
  scene: string;
  beat: number;
}

export interface Beat {
  op: string;
  template?: string;
  bindings?: Record<string, AstNode>;
  var?: string;
  expr_ast?: AstNode;
  kind?: string;
  cond_ast?: AstNode;
  t_beats?: Beat[];
  u_beats?: Beat[];
  f_beats?: Beat[];
  path_parts?: (string | number)[];
  message?: string;
  expect?: string;
  then?: Beat[];
  else?: Beat[];
  options?: ChoiceOptionBeat[];
  id?: string;
  name?: string;
  target?: { scene?: string; label?: string };
  who?: string;
  asset?: string;
  transition?: string;
  duration?: string | number;
  pos?: string;
  enter?: string;
  exit?: string;
  z?: number;
  scale?: number;
  /** base layer: "scroll" | "video" | "image" */
  mode?: string;
  /** base scroll direction: "horizontal" | "vertical" */
  scroll?: string;
  /** base scroll speed in px/s at the 1280px reference width */
  speed?: number;
  /** base/overlay layer opacity 0–1 */
  opacity?: number;
  /** base: clear the active base layer */
  clear?: boolean;
  /** object pin: normalized %-coordinates */
  x?: number;
  y?: number;
  /** object pin: "above" | "below" (front/back of characters) */
  layer?: string;
  /** object present: dialogue caption */
  caption?: string;
  /** object: explicit variant override (bypasses sprite_rules) */
  sprite?: string;
  channel?: string;
  loop?: boolean;
  fade_in?: number;
  fade_out?: number;
  seconds?: number;
  func?: string;
  arg_asts?: AstNode[];
  place_ref?: string;
  autoplay?: boolean;
  skip_after?: number;
  program?: string;
  entry?: string;
  effect?: string;
  intensity?: number;
  color?: string;
  transition_color?: string;
  anim?: string;
  icon?: string;
  var_type?: string;
  config?: Record<string, unknown>;
  result_var?: string;
  /** time op: "advance" | "set" | "wait_until" */
  action?: string;
  /** time op (advance): duration in minutes */
  minutes?: number;
  /** time op (set/wait_until): target time-of-day, 24-hour */
  t_hour?: number;
  t_minute?: number;
}

export interface ChoiceOptionBeat {
  menu?: string;
  story?: string;
  once?: boolean;
  cond_ast?: AstNode;
  branch?: Beat[];
  target?: { scene?: string; label?: string };
}

export type InterpreterState = "idle" | "running" | "waiting" | "choosing" | "ended" | "loading" | "sandbox" | "minigame";

export class Interpreter {
  readonly bb: Blackboard;
  readonly bus: EventBus;
  private _program: VnProgram | null = null;
  private _scene: string = "";
  private _beatIndex: number = 0;
  private _state: InterpreterState = "idle";
  private _deferredStart = false;
  private _sceneReady = false;
  private _pendingRun = false;
  /** Track used once-choices by choice ID + option index. */
  private _usedChoices: Set<string> = new Set();
  /** Visit counts per scene. */
  private _visits: Map<string, number> = new Map();
  /** Seen narrate/say beat IDs for skip-seen tracking. */
  private _seenBeats: Set<string> = new Set();
  /**
   * Queue of beats to execute before resuming scene beats.
   * Used for choice branches, if/then/else blocks.
   */
  private _pending: Beat[] = [];
  /** Timer handle for timed waits. */
  private _waitTimer: ReturnType<typeof setTimeout> | null = null;
  /** Called before each pause-beat (narrate/say/choice). Set by Rollback. */
  onBeforePause: ((beat: Beat) => void) | null = null;
  private _ruleEngine: RuleEngine = new RuleEngine();
  private _achievementSink: AchievementSink = new AchievementStore();
  private _achievementEngine: AchievementEngine | null = null;
  private _grantLoader: GrantLoader = GrantLoader.empty();
  private _objectSpriteEngine: ObjectSpriteEngine | null = null;
  private _entities: Entities = { characters: {}, places: {}, objects: {} };
  private _currentPlaceMusic: string | null = null;
  private _entitySyncHandler: Listener | null = null;
  private _videoPlaying = false;
  private _pausedFromPending = false;
  private _minigameResultVar: string = "_minigame_result";
  private _lastKnownPlace = "";
  private _gameClock: GameClock | null = null;

  constructor(bb: Blackboard, bus: EventBus) {
    this.bb = bb;
    this.bus = bus;
    setVisitProvider(this);
    this.bus.on("scene.ready", () => {
      this._sceneReady = true;
      if (this._pendingRun) {
        this._pendingRun = false;
        this._run();
      }
    });
  }

  notifyReady(): void {
    this._deferredStart = false;
    this._sceneReady = true;
    this._run();
  }

  get ruleEngine(): RuleEngine {
    return this._ruleEngine;
  }

  get achievementEngine(): AchievementEngine | null {
    return this._achievementEngine;
  }

  /** The active game clock, or null when the program declares no calendar. */
  get gameClock(): GameClock | null {
    return this._gameClock;
  }

  get state(): InterpreterState { return this._state; }
  get currentScene(): string { return this._scene; }
  get currentBeat(): number { return this._beatIndex; }

  /** Load a program and start at its entry point. `gameId` is the catalogue
   *  id used to key achievements/saves server-side — pass it explicitly when
   *  available (it's the platform's own game id, e.g. "sandbox_test") since
   *  `program.source` is the compiler's source filename (e.g.
   *  "sandbox_test.arc") and would silently 404 against the server's
   *  achievement routes if used as the game id. Falls back to `program.source`
   *  only for callers (mainly tests) that don't pass one. */
  load(program: VnProgram, gameId?: string): void {
    if (program.format !== "vn-program") {
      throw new Error(`Invalid program format: ${program.format}`);
    }
    this.cancelWait();
    this._program = program;
    this._scene = program.entry;
    this._beatIndex = 0;
    this._state = "running";
    this._usedChoices.clear();
    this._visits.clear();
    this._pending = [];
    this._entities = program.entities ?? { characters: {}, places: {}, objects: {} };
    this._currentPlaceMusic = null;
    this._hydrateEntities();
    this._registerEntitySync();
    this._lastKnownPlace = "";
    this._initLocationGuard();

    const globals = program.globals;
    if (globals?.functions) {
      setFunctionProvider({ functions: globals.functions });
    } else {
      setFunctionProvider(null);
    }

    const calendar = (program.meta as { calendar?: Partial<CalendarConfig> } | undefined)?.calendar;
    this._gameClock = calendar
      ? new GameClock(this.bb, buildCalendarConfig(calendar), () => this._emitTimeChanged())
      : null;
    setGameClock(this._gameClock);

    this._ruleEngine.clear();
    if (globals?.rules && globals.rules.length > 0) {
      this._ruleEngine.register(globals.rules, this.bb);
    }

    const resolvedGameId = gameId ?? (program.source as string | undefined) ?? "default";
    if (this._achievementEngine) this._achievementEngine.destroy();
    this._achievementEngine = new AchievementEngine(this.bb, this.bus, this._achievementSink, resolvedGameId);
    this._achievementEngine.load(
      (globals?.achievements ?? {}) as Record<string, AchievementDef>
    );

    if (this._objectSpriteEngine) this._objectSpriteEngine.destroy();
    this._objectSpriteEngine = new ObjectSpriteEngine(this.bb, this.bus);
    this._objectSpriteEngine.load(this._entities.objects);

    this._incrementVisits(this._scene);
    this._pendingRun = false;
    if (this._deferredStart) {
      if (this._sceneReady) {
        setTimeout(() => this._run(), 0);
      } else {
        this._pendingRun = true;
      }
    } else {
      this._run();
    }
  }

  private _hydrateEntities(): void {
    const ent = this._entities;
    for (const [id, char] of Object.entries(ent.characters)) {
      for (const [field, val] of Object.entries(char)) {
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
          this.bb.set(`char.${id}.${field}`, tagValue(val));
        }
      }
    }
    for (const [id, place] of Object.entries(ent.places)) {
      for (const [field, val] of Object.entries(place)) {
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
          this.bb.set(`place.${id}.${field}`, tagValue(val));
        }
      }
    }
    for (const [id, obj] of Object.entries(ent.objects)) {
      for (const [field, val] of Object.entries(obj)) {
        if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
          this.bb.set(`obj.${id}.${field}`, tagValue(val));
        }
      }
    }
  }

  private _registerEntitySync(): void {
    if (this._entitySyncHandler) {
      this.bb.removeGlobalListener(this._entitySyncHandler);
    }
    const handler: Listener = (key: string, value: TaggedValue) => {
      const parts = key.split(".");
      if (parts.length !== 3) return;
      const [prefix, id, field] = parts;
      const raw = untagValue(value);
      if (prefix === "char") {
        const char = this._entities.characters[id];
        if (char) (char as Record<string, unknown>)[field] = raw;
      } else if (prefix === "place") {
        const place = this._entities.places[id];
        if (place) (place as Record<string, unknown>)[field] = raw;
      } else if (prefix === "obj") {
        const obj = this._entities.objects[id];
        if (obj) obj[field] = raw;
      }
    };
    this.bb.listenAll(handler);
    this._entitySyncHandler = handler;
  }

  setDeferredStart(deferred: boolean): void {
    this._deferredStart = deferred;
  }

  /** Inject the achievement persistence backend (IndexedDB locally, server-backed
   *  remotely). Must be called before `load()` to take effect for that game. */
  setAchievementSink(sink: AchievementSink): void {
    this._achievementSink = sink;
  }

  /** Inject this game's resolved session grants (dev-spec T3). Must be called
   *  before `load()` — decl beats consult it as they execute, since there is
   *  no separate VAR-initialization pass to apply grants against up front. */
  setGrantLoader(loader: GrantLoader): void {
    this._grantLoader = loader;
  }

  /** Advance past a narrate beat (player clicked / pressed space). */
  advance(): void {
    if (this._state !== "waiting") return;
    if (this._waitTimer !== null) return;
    if (this._videoPlaying) return;
    this._state = "running";
    if (this._pending.length === 0 && !this._pausedFromPending) {
      this._beatIndex++;
    }
    this._pausedFromPending = false;
    this._run();
  }

  /** Select a choice option by index. */
  selectChoice(index: number): void {
    if (this._state !== "choosing" || !this._program) return;

    const beat = this._currentSceneBeat();
    if (!beat || beat.op !== "choice") return;

    const options = beat.options ?? [];
    if (index < 0 || index >= options.length) return;

    const option = options[index];

    // Mark as used if once
    if (option.once && beat.id) {
      this._usedChoices.add(`${beat.id}:${index}`);
    }

    this.bus.emit("choice.select", { index });

    this._state = "running";

    // Move past the choice beat
    this._beatIndex++;

    // Queue the branch beats (they execute before the next scene beat)
    if (option.branch && option.branch.length > 0) {
      this._pending = [...option.branch, ...this._pending];
    }

    // If option has a direct target, add a jump beat to the queue
    if (option.target) {
      this._pending = [{ op: "jump", target: option.target }, ...this._pending];
    }

    this._run();
  }

  /** Get the current scene beat (not pending). */
  private _currentSceneBeat(): Beat | null {
    if (!this._program) return null;
    const scene = this._program.scenes[this._scene];
    if (!scene) return null;
    if (this._beatIndex >= scene.beats.length) return null;
    return scene.beats[this._beatIndex];
  }

  /** Get the next beat to execute: pending first, then scene beats. */
  private _nextBeat(): Beat | null {
    if (this._pending.length > 0) {
      return this._pending[0];
    }
    return this._currentSceneBeat();
  }

  /** Consume a pending beat (remove from front of queue). */
  private _consumePending(): void {
    if (this._pending.length > 0) {
      this._pending.shift();
    } else {
      this._beatIndex++;
    }
  }

  private _isPauseBeat(beat: Beat): boolean {
    return beat.op === "narrate" || beat.op === "say" || beat.op === "choice";
  }

  /** Main run loop — execute beats until we pause or end. */
  private _run(): void {
    let steps = 0;
    while (this._state === "running") {
      const beat = this._nextBeat();
      if (!beat) {
        this._state = "ended";
        this.bus.emit("story.end");
        return;
      }
      if (this._isPauseBeat(beat) && this.onBeforePause) {
        this.onBeforePause(beat);
      }
      this._executeBeat(beat);
      steps++;
      if (steps > 500) {
        console.error("[Interp] SAFETY: 500 steps in single _run(), breaking");
        break;
      }
    }
  }

  /** Execute a single beat. May change state to waiting/choosing/ended. */
  private _executeBeat(beat: Beat): void {
    switch (beat.op) {
      case "narrate":
        this._execNarrate(beat);
        break;
      case "say":
        this._execSay(beat);
        break;
      case "set":
        this._execSet(beat);
        this._consumePending();
        break;
      case "time":
        this._execTime(beat);
        this._consumePending();
        break;
      case "if":
        this._execIf(beat);
        break;
      case "choice":
        this._execChoice(beat);
        break;
      case "label":
        this._consumePending();
        break;
      case "jump":
        this._execJump(beat);
        break;
      case "end":
        this._state = "ended";
        this.bus.emit("story.end");
        break;
      case "menu":
        this._execMenu(beat);
        break;
      case "minigame":
        this._execMinigame(beat);
        break;
      case "bg":
        this._execBg(beat);
        this._consumePending();
        break;
      case "base":
        this._execBase(beat);
        this._consumePending();
        break;
      case "object":
        this._execObject(beat);
        this._consumePending();
        break;
      case "sprite":
        this._execSprite(beat);
        this._consumePending();
        break;
      case "hide":
        this._execHide(beat);
        this._consumePending();
        break;
      case "play":
        this._execPlay(beat);
        this._consumePending();
        break;
      case "stop":
        this._execStop(beat);
        this._consumePending();
        break;
      case "wait":
        this._execWait(beat);
        break;
      case "call":
        this._execCall(beat);
        this._consumePending();
        break;
      case "video":
        this._execVideo(beat);
        break;
      case "scene":
        this._execScene(beat);
        break;
      case "effect":
        this._execEffect(beat);
        this._consumePending();
        break;
      case "animation":
        this._execAnimation(beat);
        this._consumePending();
        break;
      case "popup":
        this._execPopup(beat);
        this._consumePending();
        break;
      case "log":
        this._execLog(beat);
        this._consumePending();
        break;
      case "assert":
        this._execAssert(beat);
        this._consumePending();
        break;
      default:
        console.warn(`Interpreter: unknown op "${beat.op}"`);
        this._consumePending();
        break;
    }
  }

  private _execNarrate(beat: Beat): void {
    const template = beat.template ?? "";
    const resolved = resolveTemplate(template, beat.bindings, this.bb);
    if (beat.id) this._seenBeats.add(beat.id);
    this.bus.emit("dialogue.narrate", { template, resolved, id: beat.id ?? null });
    if (this._pending.length > 0 && this._pending[0] === beat) {
      this._pending.shift();
      this._pausedFromPending = true;
    } else {
      this._pausedFromPending = false;
    }
    this._state = "waiting";
  }

  private _execLog(beat: Beat): void {
    const resolved = resolveTemplate(beat.template ?? "", beat.bindings, this.bb);
    console.log(`[ArcScript] ${resolved}`);
    this.bus.emit("debug.log", { template: beat.template ?? "", resolved });
  }

  private _execAssert(beat: Beat): void {
    if (!beat.cond_ast) return;
    const result = evaluate(beat.cond_ast, this.bb);
    const ternary = result instanceof Ternary ? result : Ternary.from(result);
    if (!ternary.isTrue) {
      const msg = beat.message ?? "assertion failed";
      console.warn(`[ArcScript assert] ${msg}`);
      this.bus.emit("debug.assert", { message: msg, passed: false });
    }
  }

  private _execSet(beat: Beat): void {
    if (!beat.var || !beat.expr_ast) return;
    if (beat.kind === "decl" && beat.var_type) {
      this.bb.registerType(beat.var, beat.var_type as import("./Blackboard").VarType);
    }
    // A granted value overrides this decl's own default (dev-spec T3) — see
    // GrantLoader for why this is checked per-beat rather than in a separate
    // up-front init pass.
    const granted = beat.kind === "decl" ? this._grantLoader.get(beat.var) : undefined;
    const tv = granted ?? tagValue(evaluate(beat.expr_ast, this.bb));
    if (beat.path_parts && beat.path_parts.length > 0) {
      this.bb.setPath(beat.var, beat.path_parts, tv);
    } else {
      this.bb.set(beat.var, tv);
    }
    this.bus.emit("var.set", { name: beat.var, value: untagValue(tv) });
  }

  /**
   * Advance the game clock. `advance` adds a minute duration; `set`/`wait_until`
   * jump forward to the next occurrence of a target time-of-day. Advancing writes
   * `_clock` in catch-up steps, so `_clock`-triggered rules fire along the way.
   */
  /** Push the current clock to the HUD. Wired as the GameClock onChange hook. */
  private _emitTimeChanged(): void {
    const gc = this._gameClock;
    if (!gc) return;
    this.bus.emit("time.changed", {
      clock: gc.clock(),
      display: gc.formatted(),
      partOfDay: gc.partOfDay(),
    });
  }

  private _execTime(beat: Beat): void {
    if (!this._gameClock) return;
    const action = beat.action ?? "advance";
    if (action === "advance") {
      this._gameClock.advance(beat.minutes ?? 0);
    } else {
      // "set" / "wait_until": advance to the next occurrence of the target time
      const minutes = this._gameClock.minutesUntil(beat.t_hour ?? 0, beat.t_minute ?? 0);
      this._gameClock.advance(minutes);
    }
  }

  private _execIf(beat: Beat): void {
    // Consume the if beat BEFORE prepending branch beats to pending
    this._consumePending();

    if (!beat.cond_ast) return;

    const result = evaluate(beat.cond_ast, this.bb);
    const ternary = result instanceof Ternary ? result : Ternary.from(result);

    let branch: Beat[];
    if (ternary.isTrue) {
      branch = beat.t_beats ?? [];
    } else if (ternary.isUnknown) {
      // If ~unknown clause present, use it; otherwise fall through to false branch
      const u = beat.u_beats ?? [];
      branch = u.length > 0 ? u : (beat.f_beats ?? []);
    } else {
      branch = beat.f_beats ?? [];
    }

    if (branch.length > 0) {
      this._pending = [...branch, ...this._pending];
    }
  }

  private _execChoice(beat: Beat): void {
    const options = beat.options ?? [];
    const available: ChoiceOption[] = [];

    for (let i = 0; i < options.length; i++) {
      const opt = options[i];

      if (opt.once && beat.id && this._usedChoices.has(`${beat.id}:${i}`)) {
        continue;
      }

      let enabled = true;
      if (opt.cond_ast) {
        const condResult = evaluate(opt.cond_ast, this.bb);
        const ternary = condResult instanceof Ternary
          ? condResult
          : Ternary.from(condResult);
        if (!ternary.isTrue) enabled = false;
      }

      if (!enabled) continue;

      available.push({
        menu: opt.menu ?? "",
        story: opt.story,
        enabled: true,
        index: i,
      });
    }

    if (available.length === 0) {
      console.warn("Interpreter: all choice options hidden. Treating as end.");
      this._state = "ended";
      this.bus.emit("story.end");
      return;
    }

    this._state = "choosing";
    this.bus.emit("choice.show", { options: available });
  }

  private _execJump(beat: Beat): void {
    if (!beat.target) return;
    // Clear pending — jump abandons any remaining queued beats
    this._pending = [];
    this._jump(beat.target);
  }

  private _jump(target: { scene?: string; label?: string }): void {
    if (!this._program) return;

    if (target.label && target.scene) {
      const labelKey = `${target.scene}.${target.label}`;
      const ref = this._program.labels[labelKey];
      if (ref) {
        if (ref.scene !== this._scene) this._incrementVisits(ref.scene);
        this._scene = ref.scene;
        this._beatIndex = ref.beat;
        this._applySceneLocation();
        return;
      }
    }

    if (target.scene) {
      const ref = this._program.labels[target.scene];
      if (ref) {
        if (ref.scene !== this._scene) this._incrementVisits(ref.scene);
        this._scene = ref.scene;
        this._beatIndex = ref.beat;
        this._applySceneLocation();
        return;
      }

      if (this._program.scenes[target.scene]) {
        if (target.scene !== this._scene) this._incrementVisits(target.scene);
        this._scene = target.scene;
        this._beatIndex = 0;
        this._applySceneLocation();
        return;
      }
    }

    console.warn(`Interpreter: jump target not found:`, target);
    this._beatIndex++;
  }

  private _execSay(beat: Beat): void {
    const who = beat.who ?? "";
    const charEntity = this._entities.characters[who];
    const displayName = charEntity?.display_name ?? charEntity?.name ?? who;
    const color = charEntity?.color;

    if (charEntity?.voice) {
      this.bus.emit("audio.play", { channel: "voice", asset: charEntity.voice });
    }

    const template = beat.template ?? "";
    const resolved = resolveTemplate(template, beat.bindings, this.bb);
    if (beat.id) this._seenBeats.add(beat.id);
    this.bus.emit("dialogue.say", { who: displayName, template, resolved, color, id: beat.id ?? null });
    if (this._pending.length > 0 && this._pending[0] === beat) {
      this._pending.shift();
      this._pausedFromPending = true;
    } else {
      this._pausedFromPending = false;
    }
    this._state = "waiting";
  }

  private _execBg(beat: Beat): void {
    let asset = beat.asset ?? "";
    const placeRef = beat.place_ref;

    if (!asset && placeRef) {
      const place = this._entities.places[placeRef];
      if (place) {
        asset = place.image
          ?? resolveBackground(place.background, undefined, this._gameClock?.partOfDay())
          ?? "";
      }
    }

    const transition = beat.transition;
    const raw = beat.duration;
    const duration = typeof raw === "string" ? DURATION_NAMES[raw] ?? parseFloat(raw) : raw;
    this.bus.emit("bg.set", { asset, transition, duration, transition_color: beat.transition_color, place_ref: placeRef });

    if (placeRef) {
      const place = this._entities.places[placeRef];
      const placeMusic = place?.music;
      if (placeMusic && placeMusic !== this._currentPlaceMusic) {
        this._currentPlaceMusic = placeMusic;
        this.bus.emit("audio.play", { channel: "bgm", asset: placeMusic, loop: true, fade_in: 1.0 });
      }
    }
  }

  private _execBase(beat: Beat): void {
    if (beat.clear) {
      this.bus.emit("base.clear");
      return;
    }
    this.bus.emit("base.set", {
      asset: beat.asset ?? "",
      mode: beat.mode,
      scroll: beat.scroll,
      speed: beat.speed,
      opacity: beat.opacity,
      loop: beat.loop,
    });
  }

  private _execObject(beat: Beat): void {
    const id = beat.id ?? "";
    if (!id) return;
    const action = beat.action ?? "present";

    if (action === "hide") {
      this.bus.emit("object.hide", { id });
      return;
    }

    const entity = this._entities.objects[id];
    const variant = beat.sprite ?? resolveObjectVariant(entity, this.bb);
    const asset = resolveObjectAsset(entity, variant);

    if (action === "pin") {
      this.bus.emit("object.show", {
        id,
        asset,
        x: beat.x ?? 50,
        y: beat.y ?? 90,
        layer: beat.layer ?? "above",
      });
    } else {
      this.bus.emit("object.present", {
        id,
        asset,
        caption: beat.caption,
        exit: beat.exit ?? "return",
      });
    }
  }

  private _execSprite(beat: Beat): void {
    const id = beat.id ?? "";
    let asset = beat.asset ?? "";
    if (!asset && id) {
      const charEntity = this._entities.characters[id];
      if (charEntity?.default_sprite) {
        asset = charEntity.default_sprite;
        if (!asset.includes(".")) asset += ".png";
      }
    }
    this.bus.emit("sprite.show", {
      id,
      asset,
      pos: beat.pos,
      enter: beat.enter,
      z: beat.z,
      scale: beat.scale,
    });
  }

  private _execHide(beat: Beat): void {
    const id = beat.id ?? "";
    const raw = beat.duration;
    const duration = typeof raw === "string" ? DURATION_NAMES[raw] ?? parseFloat(raw) : raw;
    this.bus.emit("sprite.hide", { id, exit: beat.exit, duration });
  }

  private _execPlay(beat: Beat): void {
    const channel = beat.channel ?? "sfx";
    const asset = beat.asset ?? "";
    this.bus.emit("audio.play", {
      channel,
      asset,
      loop: beat.loop,
      fade_in: beat.fade_in,
    });
  }

  private _execStop(beat: Beat): void {
    const channel = beat.channel ?? "sfx";
    this.bus.emit("audio.stop", { channel, fade_out: beat.fade_out });
  }

  private _execWait(beat: Beat): void {
    const seconds = beat.seconds ?? 0;
    this._consumePending();
    this.bus.emit("wait.start", { seconds });
    this._state = "waiting";
    this._waitTimer = setTimeout(() => {
      this._waitTimer = null;
      if (this._state === "waiting") {
        this.bus.emit("wait.end");
        this._state = "running";
        this._run();
      }
    }, seconds * 1000);
  }

  private _execVideo(beat: Beat): void {
    const asset = beat.asset ?? "";
    const autoplay = beat.autoplay ?? true;
    const skip_after = beat.skip_after;
    this._consumePending();
    this.bus.emit("video.play", { asset, autoplay, skip_after });
    this._state = "waiting";
    this._videoPlaying = true;
    const unsub = this.bus.on("video.end", () => {
      unsub();
      this._videoPlaying = false;
      if (this._state === "waiting") {
        this._state = "running";
        this._run();
      }
    });
  }

  private _execCall(beat: Beat): void {
    const func = beat.func ?? "";
    const argValues = (beat.arg_asts ?? []).map((ast) => evaluate(ast, this.bb));
    this.bus.emit("call.exec", { func, args: argValues });
    if (!this._program) return;

    const fnDef = this._program?.globals?.functions?.find((f) => f.name === func);
    if (!fnDef) {
      console.warn(`Interpreter: function "${func}" not found`);
      return;
    }
    if (!fnDef.body_ast) {
      console.warn(`Interpreter: function "${func}" has no evaluable body`);
      return;
    }

    callFunction(this.bb, fnDef, argValues);
  }

  private _initLocationGuard(): void {
    this.bb.listen("_current_place", (_key, newTagged) => {
      const newVal = String(untagValue(newTagged) ?? "");
      const allowTeleport = untagValue(this.bb.get("_allow_teleport"));
      if (allowTeleport === true) return;

      const prevVal = this._lastKnownPlace;
      if (prevVal && newVal && prevVal !== newVal) {
        const prevPlace = this._entities.places?.[prevVal];
        const exits = (prevPlace?.exits ?? []).map((e: unknown) =>
          typeof e === "string" ? e : (e as { target: string }).target
        );
        if (exits.length > 0 && !exits.includes(newVal)) {
          console.warn(
            `[ArcTree] Location guard: "${prevVal}" has no exit to "${newVal}". ` +
            `Set _allow_teleport = True to suppress.`
          );
        }
        // Reset orientation/position — exits carry no implied values
        this.bb.set("_current_azimuth", tagValue(null));
        this.bb.set("_current_x",       tagValue(null));
        this.bb.set("_current_y",       tagValue(null));
      }
      this._lastKnownPlace = newVal;
    });
  }

  private _applySceneLocation(): void {
    if (!this._program) return;
    const scene = this._program.scenes[this._scene] as Scene | undefined;
    if (!scene) return;

    const declaredPlace = scene.location;
    if (declaredPlace && declaredPlace !== this._lastKnownPlace) {
      // Crossing a place boundary without an explicit ~set _current_place —
      // reset orientation/position and update tracking
      this.bb.set("_current_azimuth", tagValue(null));
      this.bb.set("_current_x",       tagValue(null));
      this.bb.set("_current_y",       tagValue(null));
      this._lastKnownPlace = declaredPlace;
    }

    if (scene.azimuth !== undefined) this.bb.set("_current_azimuth", tagValue(scene.azimuth));
    if (scene.x       !== undefined) this.bb.set("_current_x",       tagValue(scene.x));
    if (scene.y       !== undefined) this.bb.set("_current_y",       tagValue(scene.y));
  }

  private _execScene(beat: Beat): void {
    const program = beat.program ?? "";
    const entry = beat.entry;
    this._consumePending();
    this._state = "loading";
    this.bus.emit("scene.load", { program, entry });
  }

  private _execEffect(beat: Beat): void {
    const effect = beat.effect ?? "";
    const raw = beat.duration;
    const duration = typeof raw === "string" ? DURATION_NAMES[raw] ?? parseFloat(raw) : raw;
    this.bus.emit("effect.run", { effect, intensity: beat.intensity, duration, color: beat.color });
  }

  private _execAnimation(beat: Beat): void {
    const id = beat.id ?? "";
    const anim = beat.anim ?? "";
    const raw = beat.duration;
    const duration = typeof raw === "string" ? DURATION_NAMES[raw] ?? parseFloat(raw) : raw;
    this.bus.emit("animation.run", { id, anim, duration });
  }

  private _execPopup(beat: Beat): void {
    const template = beat.template ?? "";
    const text = resolveTemplate(template, beat.bindings, this.bb);
    const icon = beat.icon;
    const raw = beat.duration;
    const duration = typeof raw === "string" ? DURATION_NAMES[raw] ?? parseFloat(raw) : (raw as number | undefined) ?? 3;
    this.bus.emit("popup.show", { text, icon, duration });
  }

  private _execMenu(beat: Beat): void {
    this._consumePending();
    let place: string;
    if (this.bb.has("_current_place")) {
      place = String(untagValue(this.bb.get("_current_place")));
    } else {
      place = this._resolveCurrentPlace();
      if (place) {
        this.bb.set("_current_place", tagValue(place));
      }
    }
    // v2: time passes during the narrative chain just played. Per-menu `MENU(n)`
    // overrides the global `menu_cost`. Applied before `sandbox.enter` so the
    // HUD shows the post-chain time.
    if (this._gameClock) {
      const cost = beat.minutes ?? this._gameClock.cfg.menu_cost;
      if (cost > 0) this._gameClock.advance(cost);
    }
    this._state = "sandbox";
    this.bus.emit("sandbox.enter", { place });
  }

  private _resolveCurrentPlace(): string {
    const places = this._entities.places;
    const ids = Object.keys(places);
    return ids.length > 0 ? ids[0] : "";
  }

  /** Exit sandbox mode and resume narrative at target scene. */
  exitSandbox(scene: string): void {
    if (this._state !== "sandbox") return;
    this.bus.emit("sandbox.exit", { scene });
    this._state = "running";
    this._pending = [];
    this._jump({ scene });
    this._run();
  }

  private _execMinigame(beat: Beat): void {
    const id = beat.id ?? "";
    const config = beat.config ?? {};
    this._minigameResultVar = beat.result_var ?? "_minigame_result";
    this._consumePending();
    this._state = "minigame";
    this.bus.emit("minigame.start", { id, config });
  }

  /** Exit minigame mode and resume narrative at the next beat. An optional exit
   *  `delta` (combat design §6) is applied to the inventory once, here — outside
   *  the sandbox — so gains/losses land only on a real completion (save/load
   *  clean; a mid-game reload before completion never double-applies). */
  exitMinigame(result: string, delta?: MinigameDelta): void {
    if (this._state !== "minigame") return;
    this.bb.set(this._minigameResultVar, tagValue(result));
    if (delta) this._applyMinigameDelta(delta);
    this._state = "running";
    this._run();
  }

  /** Apply a minigame exit delta to the `_inventory` blackboard array (the same
   *  store InventoryManager / `has_item` read). Additions always land; the
   *  subtract side (`consumed`/`ammo_deductions`) only bites when the loadout is
   *  inventory-sourced, so it's a no-op today (the ids aren't held). */
  private _applyMinigameDelta(delta: MinigameDelta): void {
    const raw = this.bb.has("_inventory") ? untagValue(this.bb.get("_inventory")) : [];
    const ids: string[] = Array.isArray(raw) ? raw.map(String) : [];
    for (const id of delta.gained_items ?? []) {
      ids.push(id);
      this.bb.set(`_place_${id}`, tagValue("_inventory"));
    }
    for (const id of delta.consumed ?? []) {
      const i = ids.indexOf(id);
      if (i !== -1) ids.splice(i, 1);
    }
    for (const [id, qty] of Object.entries(delta.ammo_deductions ?? {})) {
      for (let n = 0; n < qty; n++) {
        const i = ids.indexOf(id);
        if (i === -1) break;
        ids.splice(i, 1);
      }
    }
    this.bb.set("_inventory", tagValue(ids));
  }

  /** Cancel an active wait timer or video playback (used during cleanup / rollback). */
  cancelWait(): void {
    if (this._waitTimer !== null) {
      clearTimeout(this._waitTimer);
      this._waitTimer = null;
    }
    this._videoPlaying = false;
  }

  /** Resume from a loading state after a scene has been loaded externally. */
  loadScene(program: VnProgram, entry?: string): void {
    if (this._state !== "loading") return;
    this._program = program;
    this._scene = entry ?? program.entry;
    this._beatIndex = 0;
    this._pending = [];
    this._incrementVisits(this._scene);
    this._state = "running";
    this._run();
  }

  /** Get the visit count for a scene. */
  getVisits(scene: string): number {
    return this._visits.get(scene) ?? 0;
  }

  private _incrementVisits(scene: string): void {
    this._visits.set(scene, (this._visits.get(scene) ?? 0) + 1);
  }

  // --- Snapshot / restore support for Rollback ---

  getVisitsMap(): Record<string, number> {
    const out: Record<string, number> = {};
    for (const [k, v] of this._visits) out[k] = v;
    return out;
  }

  getUsedChoices(): string[] {
    return [...this._usedChoices];
  }

  getSeenBeats(): string[] {
    return [...this._seenBeats];
  }

  restoreSeenBeats(ids: string[]): void {
    this._seenBeats = new Set(ids);
  }

  isSeenBeat(id: string): boolean {
    return this._seenBeats.has(id);
  }

  getPending(): Beat[] {
    return [...this._pending];
  }

  getProgram(): VnProgram | null {
    return this._program;
  }

  restoreState(
    scene: string,
    beat: number,
    pending: Beat[],
    visits: Record<string, number>,
    usedChoices: string[],
    seenBeats: string[] = [],
  ): void {
    this.cancelWait();
    this._scene = scene;
    this._beatIndex = beat;
    this._pending = [...pending];
    this._visits = new Map(Object.entries(visits));
    this._usedChoices = new Set(usedChoices);
    this._seenBeats = new Set(seenBeats);
    this._state = "running";
    this._run();
  }

  /** Teleport to a specific scene and beat index. Used by debug panel. */
  teleport(scene: string, beat?: number): void {
    if (!this._program) return;
    if (!this._program.scenes[scene]) {
      console.warn(`Interpreter.teleport: scene "${scene}" not found`);
      return;
    }
    this.cancelWait();
    this._pending = [];
    this._scene = scene;
    this._beatIndex = beat ?? 0;
    this._incrementVisits(scene);
    this._state = "running";
    this._run();
  }
}
